/**
 * Sidebar sections for the construction tenant.
 *
 * The flat, admin-ordered menu is folded under four plainly named sections so
 * a customer user sees Dashboard plus four headings, with only the section
 * they are working in opened out. Items are assigned by their route; anything
 * no section claims (and every supplier-side item) renders ungrouped in its
 * configured order, exactly as before. Applied only while the monthly upload
 * cycle is on, so other tenants keep their flat sidebar.
 */
import { BarChart3, ShoppingCart, Users, FolderOpen, type LucideIcon } from 'lucide-react';
import type { NavItem } from './navigation';

export interface NavGroupDef {
  key: string;
  name: string;
  /** One line under the heading's tooltip saying what lives there. */
  hint: string;
  icon: LucideIcon;
  /** Routes (NavItem.href) that belong to the section. */
  hrefs: string[];
}

/** Always shown above the sections, never folded away. */
export const PINNED_HREFS = ['/dashboard'];

export const CUSTOMER_NAV_GROUPS: NavGroupDef[] = [
  {
    key: 'performance',
    name: 'Performance',
    hint: 'Supplier performance, spend leakage, price variance and division scores',
    icon: BarChart3,
    hrefs: ['/barratt/supplier-performance', '/suppliers/performance', '/spend', '/division-scoring',
            '/barratt/spend-data', '/barratt/call-off-performance', '/reports'],
  },
  {
    key: 'buying',
    name: 'Buying',
    hint: 'Orders, contracts, pricebooks, lead times and parts',
    icon: ShoppingCart,
    hrefs: ['/purchasing', '/contracts', '/parts-master', '/part-schedule', '/rfq'],
  },
  {
    key: 'suppliers',
    name: 'Suppliers',
    hint: 'The supplier directory, onboarding, questionnaires, audits and the calendar',
    icon: Users,
    hrefs: ['/suppliers', '/onboarding', '/questionnaires', '/audits', '/calendar', '/quality', '/delivery-appeals'],
  },
  {
    key: 'files',
    name: 'Files & documents',
    hint: 'Monthly supplier files, the invoice file and the document library',
    icon: FolderOpen,
    hrefs: ['/monthly-files', '/documents', '/news', '/data-import', '/import-monitor', '/user-activity'],
  },
];

export interface NavGroup {
  def: NavGroupDef;
  items: NavItem[];
}

export interface GroupedNavigation {
  /** Items shown above the sections (Dashboard). */
  pinned: NavItem[];
  groups: NavGroup[];
  /** Items no section claims, rendered flat after the sections. */
  rest: NavItem[];
}

function matches(item: NavItem, href: string): boolean {
  return item.href === href || item.href.startsWith(`${href}?`);
}

/** Partition an ordered flat menu into pinned items, sections and the rest, keeping order inside each. */
export function groupNavigation(items: NavItem[], defs: NavGroupDef[] = CUSTOMER_NAV_GROUPS): GroupedNavigation {
  const pinned = items.filter((item) => PINNED_HREFS.some((href) => matches(item, href)));
  const claimed = new Set<NavItem>(pinned);
  const groups: NavGroup[] = defs.map((def) => {
    const members = items.filter((item) => !claimed.has(item) && def.hrefs.some((href) => matches(item, href)));
    members.forEach((m) => claimed.add(m));
    return { def, items: members };
  }).filter((g) => g.items.length > 0);
  return { pinned, groups, rest: items.filter((item) => !claimed.has(item)) };
}
